import { useLocale } from '@app/core/hooks/useLocale';
import { Toggle } from '@app/shared/components/toggle/Toggle';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styles from './expand.module.scss';
import {
  CapitalStructureCapTableEventDto,
  CapitalStructureEventDto,
  CapitalStructureInstrumentDefinitionDto,
} from '@app/shared/models/contracts/capital-structure-debt-instrument-dto';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { ExpandCapTable } from './ExpandCapTable';
import { ExpandInstrumentTable } from './ExpandInstrumentTable';
import { EventKey, InstrumentType } from '@app/shared/models/contracts/enums/shared-enums';
import { getEquityInstrumentCountByType } from '@app/shared/helpers/equity-instruments/get-equity-instrument-count-by-type';
import { getCarryingValueClarificationKey } from '@app/shared/helpers/equity-instruments/get-carrying-value-clarification-key';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import {
  setExpandStructureInstruments,
  setExpandStructureSelectedRadio,
} from '@app/core/store/ui-values-slice';

interface ExpandStructureProps {
  capTableEvent: CapitalStructureCapTableEventDto | undefined;
  buildStructureEvent: CapitalStructureEventDto;
  instrumentDefinitions: Record<string, CapitalStructureInstrumentDefinitionDto>;
}

enum ExpandType {
  CarryingValues = 'values',
  ChangesByInstrument = 'instrument',
}

export const ExpandStructure: FC<ExpandStructureProps> = ({
  capTableEvent,
  buildStructureEvent,
  instrumentDefinitions,
}) => {
  const { l } = useLocale();
  const dispatch = useAppDispatch();
  const storedInstruments = useAppSelector(
    (state) =>
      state.uiValues.userSelections.capitalStructure.expandStructureInstruments[
        buildStructureEvent.id
      ]
  );
  const storedSelectedRadio = useAppSelector(
    (state) =>
      state.uiValues.userSelections.capitalStructure.expandStructureSelectedRadio[
        buildStructureEvent.id
      ]
  );
  const [toggleExpanded, setToggleExpanded] = useState({
    ignoreExpandRows: false,
    toggleExpanded: false,
  });
  const [selectedRadio, setSelectedRadio] = useState<ExpandType>(
    (storedSelectedRadio as ExpandType) ?? ExpandType.CarryingValues
  );
  const [shouldResetExpandedInstruments, setShouldResetExpandedInstruments] = useState(false);

  const getDefaultExpandedInstruments = useCallback((): Dictionary<boolean> => {
    const defaultExpandedInstruments: Dictionary<boolean> = {};

    switch (selectedRadio) {
      case ExpandType.CarryingValues:
        if (capTableEvent) {
          Object.keys(capTableEvent.instruments).forEach((key) => {
            defaultExpandedInstruments[key] = false;
          });
        }
        break;
      case ExpandType.ChangesByInstrument:
        if (buildStructureEvent) {
          Object.values(buildStructureEvent.movements.adjustments.instrumentTypeSummary).forEach(
            (instrumentType) => {
              Object.keys(instrumentType.totalInstrument).forEach((key) => {
                defaultExpandedInstruments[key] = false;
              });
            }
          );
        }
        break;
    }

    return defaultExpandedInstruments;
  }, [buildStructureEvent, capTableEvent, selectedRadio]);

  const [expandedInstruments, setExpandedInstruments] = useState<Dictionary<boolean>>(
    storedInstruments && Object.keys(storedInstruments).length > 0
      ? storedInstruments
      : getDefaultExpandedInstruments()
  );

  useEffect(() => {
    if (shouldResetExpandedInstruments) {
      setShouldResetExpandedInstruments(false);
      setExpandedInstruments(getDefaultExpandedInstruments());
    }
  }, [getDefaultExpandedInstruments, selectedRadio, shouldResetExpandedInstruments]);

  useEffect(() => {
    dispatch(
      setExpandStructureSelectedRadio({
        eventId: buildStructureEvent.id,
        selectedRadio,
      })
    );
  }, [dispatch, buildStructureEvent.id, selectedRadio]);

  useEffect(() => {
    dispatch(
      setExpandStructureInstruments({
        eventId: buildStructureEvent.id,
        instruments: expandedInstruments,
      })
    );
  }, [dispatch, buildStructureEvent.id, expandedInstruments]);

  const expandAllRows = (isExpandAllRows: boolean) => {
    if (!capTableEvent) {
      return;
    }

    const newExpandedInstruments = { ...expandedInstruments };
    Object.keys(newExpandedInstruments).forEach((key) => {
      newExpandedInstruments[key] = isExpandAllRows;
    });

    setExpandedInstruments(newExpandedInstruments);
    setToggleExpanded({ toggleExpanded: isExpandAllRows, ignoreExpandRows: false });
  };

  useEffect(() => {
    const allTrue = Object.values(expandedInstruments).every((value) => value);
    if (!allTrue) {
      setToggleExpanded((prevState) => {
        if (!prevState.toggleExpanded && prevState.ignoreExpandRows) {
          return prevState;
        }
        return { toggleExpanded: false, ignoreExpandRows: true };
      });
    }
    if (allTrue) {
      setToggleExpanded((prevState) => {
        if (prevState.toggleExpanded && prevState.ignoreExpandRows) {
          return prevState;
        }
        return { toggleExpanded: true, ignoreExpandRows: true };
      });
    }
  }, [expandedInstruments]);

  const formMethods = useForm<any>({
    ...formConfigBase,
  });

  const instruments = Object.entries(capTableEvent?.instruments ?? {}).map(([key, value]) => ({
    ...value,
    instrumentId: key,
  }));

  const carryingValueClarificationKey = getCarryingValueClarificationKey(
    getEquityInstrumentCountByType(instruments, InstrumentType.PreferredShares) > 0,
    getEquityInstrumentCountByType(instruments, InstrumentType.ShareholderLoanNotes) > 0
  );

  if (!capTableEvent) {
    return (
      <>
        <div className={styles['expanded-wrapper']}>
          <h3>{l('_UnableToRenderExtender')}</h3>
          <p>{l('_PleaseFixOutstandingIssuesAndTryAgain')}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <FormProvider {...formMethods}>
        <div className={styles['expanded-wrapper']}>
          {buildStructureEvent.id !== EventKey.InitialCapitalStructure && (
            <div className={styles['radio-wrapper']}>
              <RadioGroup row key="expandType" name="expandType" value={selectedRadio}>
                <FormControlLabel
                  value={ExpandType.CarryingValues}
                  control={<Radio />}
                  label={l('_CarryingValues')}
                  onChange={() => {
                    setToggleExpanded({ toggleExpanded: false, ignoreExpandRows: false });
                    setShouldResetExpandedInstruments(true);
                    setSelectedRadio(ExpandType.CarryingValues);
                  }}
                />
                <FormControlLabel
                  value={ExpandType.ChangesByInstrument}
                  control={<Radio />}
                  label={l('_ChangesByInstrument')}
                  onChange={() => {
                    setToggleExpanded({ toggleExpanded: false, ignoreExpandRows: false });
                    setShouldResetExpandedInstruments(true);
                    setSelectedRadio(ExpandType.ChangesByInstrument);
                  }}
                />
              </RadioGroup>
            </div>
          )}
          {(selectedRadio !== ExpandType.ChangesByInstrument ||
            buildStructureEvent.movements.adjustments.total.amount !== 0 ||
            buildStructureEvent.movements.adjustments.total.numberOfShares !== 0) && (
            <div className={styles['toggle-wrapper']}>
              <Toggle
                name="expandAllRows"
                label={l('_ExpandAllRows')}
                isLabelHidden
                onClick={() => expandAllRows(!toggleExpanded.toggleExpanded)}
                forceChecked={toggleExpanded.toggleExpanded}
              />
              <div>
                <span className={styles['toggle-text']}>{l('_ExpandAllRows')}</span>
              </div>
            </div>
          )}

          <div className={styles['table-wrapper']}>
            {selectedRadio === ExpandType.CarryingValues && (
              <>
                <ExpandCapTable
                  capTableEvent={capTableEvent}
                  expandedInstruments={expandedInstruments}
                  setExpandedInstruments={setExpandedInstruments}
                />
                {carryingValueClarificationKey && (
                  <p className={styles['italic-text']}>{l(carryingValueClarificationKey)}</p>
                )}
              </>
            )}
            {selectedRadio === ExpandType.ChangesByInstrument && (
              <ExpandInstrumentTable
                expandedInstruments={expandedInstruments}
                setExpandedInstruments={setExpandedInstruments}
                buildStructureEvent={buildStructureEvent}
                instrumentDefinitions={instrumentDefinitions}
              />
            )}
          </div>
        </div>
      </FormProvider>
    </>
  );
};
